import { useScrollLock } from "@/hooks/useScrollLock";
import type { ReputationKeywordDetailData } from "@/services/api/reputationDetail";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { Button, Grid, Modal, Tag } from "antd";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import TextFormatter from "../../utils/textFormatter";
import CitelistModal from "./CitelistModal";

interface ReputationDetailModalProps {
  open: boolean;
  onClose: () => void;
  loading?: boolean;
  data?: ReputationKeywordDetailData | null;
  keyword?: string;
  sentiment?: "positive" | "negative";
  taskIds?: string[];
  currentIndex?: number;
  // 新增：数据总数属性
  total?: number;
  onPrevQuestion?: () => void;
  onNextQuestion?: () => void;
}

const ReputationDetailModal: React.FC<ReputationDetailModalProps> = ({
  open,
  onClose,
  loading,
  data,
  keyword,
  sentiment,
  taskIds = [],
  currentIndex = 0,
  // 新增：解构数据总数属性
  total,
  onPrevQuestion,
  onNextQuestion,
}) => {
  const { t } = useTranslation();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const [citelistModalVisible, setCitelistModalVisible] = useState(false);

  // 防止移动端滚动穿透
  useScrollLock(open);

  // 计算导航按钮状态
  const hasPrevQuestion = currentIndex > 0;
  // 使用传入的total属性，如果没有则回退到taskIds.length
  const totalQuestions = total !== undefined ? total : taskIds.length;
  const hasNextQuestion = currentIndex < totalQuestions - 1;

  // 局部样式（沿用 QABrowserModal 的视觉体系）
  useEffect(() => {
    if (!open) return;
    const styleId = "reputation-detail-modal-styles";
    if (document.getElementById(styleId)) return;
    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = `
      /* 背景蒙层 - 模糊与渐变 */
      .reputation-detail-modal .ant-modal-mask {
        background: linear-gradient(135deg, rgba(43,189,178,0.10) 0%, rgba(15,108,117,0.15) 50%, rgba(43,189,178,0.10) 100%);
        backdrop-filter: blur(8px);
      }
      .reputation-detail-modal .ant-modal-content {
        background: rgba(255,255,255,0.9);
        backdrop-filter: blur(18px);
        border: 1px solid rgba(43,189,178,0.2);
        border-radius: 6px;
        overflow: hidden;
        box-shadow: 0 25px 50px -12px rgba(43,189,178,0.15);
      }
      .reputation-detail-modal .ant-modal-close {
        right: 14px; top: 14px;
        color: #475569;
        background: rgba(255,255,255,0.6);
        border-radius: 50%; width: 32px; height: 32px;
        display: flex; align-items: center; justify-content: center;
        transition: all .2s ease;
        border: 1px solid rgba(255,255,255,0.3);
        backdrop-filter: blur(8px);
      }
      .reputation-detail-modal .ant-modal-close:hover { color: #4BA67A; transform: scale(1.05); }
      .rep-detail-title { font-weight: 800; letter-spacing: .2px; }
      .rep-detail-meta { color: #64748b; }
      .rep-detail-answer { white-space: pre-wrap; line-height: 1.75; color: #0f172a }
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
    return () => {
      const el = document.getElementById(styleId);
      if (el) document.head.removeChild(el);
    };
  }, [open]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={isMobile ? "92vw" : "90%"}
      centered
      title={null}
      rootClassName="reputation-detail-modal"
      styles={{
        content: { padding: isMobile ? 12 : 24 },
        body: { maxHeight: "200vh", overflow: "auto", padding: 0 },
      }}
    >
      {/* 标题和标签 */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          margin: isMobile ? "8px 16px 8px" : "12px 20px 8px",
        }}
      >
        <div
          className="rep-detail-title"
          style={{ fontSize: isMobile ? 18 : 20 }}
        >
          {keyword
            ? `与"${keyword}"相关的问答`
            : t("common.modals.reputation_default_title")}
        </div>
        {sentiment && (
          <Tag color={sentiment === "positive" ? "green" : "red"}>
            {sentiment === "positive"
              ? "正面"
              : t("common.modals.reputation_negative_tag")}
          </Tag>
        )}
      </div>

      {/* 导航按钮 - 独立一行 */}
      {totalQuestions > 1 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 12,
            margin: isMobile ? "0 0px 12px" : "0 20px 12px",
            padding: "8px 0",
            background: "rgba(43,189,178,0.05)",
            borderRadius: 6,
            border: "1px solid rgba(43,189,178,0.1)",
          }}
        >
          <Button
            size="small"
            icon={<LeftOutlined />}
            disabled={!hasPrevQuestion}
            onClick={onPrevQuestion}
            style={{
              borderRadius: 6,
              border: "1px solid #d1d5db",
              background: hasPrevQuestion ? "#ffffff" : "#f9fafb",
              color: hasPrevQuestion ? "#374151" : "#9ca3af",
              minWidth: 80,
            }}
          >
            上一个
          </Button>

          <span
            style={{
              fontSize: 14,
              color: "#64748b",
              fontWeight: 500,
              minWidth: 60,
              textAlign: "center",
            }}
          >
            {currentIndex + 1} / {totalQuestions}
          </span>

          <Button
            size="small"
            icon={<RightOutlined />}
            disabled={!hasNextQuestion}
            onClick={onNextQuestion}
            style={{
              borderRadius: 6,
              border: "1px solid #d1d5db",
              background: hasNextQuestion ? "#ffffff" : "#f9fafb",
              color: hasNextQuestion ? "#374151" : "#9ca3af",
              minWidth: 80,
            }}
          >
            下一个
          </Button>
        </div>
      )}

      {/* 问题摘要 */}
      <div
        style={{
          background: "rgba(255,255,255,0.7)",
          border: "1px solid rgba(43,189,178,0.2)",
          borderRadius: 6,
          padding: isMobile ? 12 : 16,
          margin: isMobile ? "0 0px 12px" : "0 20px 12px",
        }}
      >
        <div className="rep-detail-meta" style={{ marginBottom: 8 }}>
          {data?.createTime
            ? new Date(data.createTime).toLocaleDateString()
            : "-"}{" "}
          · {data?.modelName || t("common.modals.reputation_model_tag")}
        </div>
        <div style={{ fontWeight: 600, color: "#0f172a" }}>
          {data?.question || "..."}
        </div>
      </div>

      {/* 回答内容容器 */}
      <div
        style={{
          background: "#f8fafc",
          border: "1px solid rgba(43,189,178,0.2)",
          borderRadius: 6,
          padding: 0,
          margin: isMobile ? "0 0px 16px" : "0 20px 20px",
          maxHeight: "calc(100vh - 220px)",
          overflowY: "auto",
          minHeight: "120px",
          position: "relative",
        }}
      >
        {/* 可点击文字行 - 现在在滚动容器内 */}
        <div
          style={{
            padding: isMobile ? "12px 16px" : "16px 20px",
            backgroundColor: "rgba(248, 250, 252, 0.8)",
            cursor: "pointer",
            transition: "background-color 0.2s ease",
            borderBottom: "1px solid rgba(43,189,178,0.1)",
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.backgroundColor = "rgba(43, 189, 178, 0.1)")
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.backgroundColor = "rgba(248, 250, 252, 0.8)")
          }
          onClick={() => {
            setCitelistModalVisible(true);
          }}
        >
          <div
            style={{
              color: "#21C4B8",
              fontSize: 14,
              fontWeight: 500,
              textAlign: "left",
            }}
          >
            {t("common.modals.reputation_search_web")}（{data?.linksNum || 0}）
            {/* {t("common.modals.reputation_search_web_suffix")} */}
          </div>
        </div>

        {/* 回答内容区 */}
        <div style={{ padding: isMobile ? 12 : 16 }}>
          {loading ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "150px",
                color: "#64748b",
                fontSize: 14,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  background: "rgba(43,189,178,0.1)",
                  padding: "8px 16px",
                  borderRadius: 6,
                  border: "1px solid rgba(43,189,178,0.2)",
                }}
              >
                <div
                  style={{
                    width: 16,
                    height: 16,
                    border: "2px solid #21C4B8",
                    borderTop: "2px solid transparent",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                  }}
                />
                {t("search_detail.competitor_loading")}
              </div>
            </div>
          ) : (
            <div
              style={{
                opacity: data ? 1 : 0.7,
                transition: "opacity 0.2s ease-in-out",
              }}
            >
              <TextFormatter
                content={
                  data?.answer
                    // 处理 [1,3,5,7](@ref) 格式，拆分成单独的 [数字]
                    .replace(
                      /\[([\d,]+)\]\(@ref\)/g,
                      (match: string, numbers: string) => {
                        const numberArray = numbers.split(",");
                        return numberArray
                          .map((num: string) => `[${num.trim()}]`)
                          .join(" ");
                      }
                    )
                    .replace(/\[citation:(\d+)\]/g, " [$1]")
                    .replace(/<br\s*\/?>/gi, "；")
                    .replace(/\[\^(\d+)\^\]/g, " [$1]") ||
                  t("common.modals.no_content")
                }
              />
            </div>
          )}
        </div>
      </div>

      {/* CitelistModal */}
      <CitelistModal
        open={citelistModalVisible}
        onClose={() => setCitelistModalVisible(false)}
        title={t("search_detail.insight_related_articles")}
        taskId={data?.taskId}
        brandId={data?.brandId}
        categoryId={data?.categoryId}
        days={data?.days}
        metricType={data?.metricType}
        modelId={data?.modelId}
        onItemClick={(item) => {
          // 处理文章点击事件，在新窗口打开
          window.open(item.url, "_blank");
        }}
      />
    </Modal>
  );
};

export default ReputationDetailModal;
